import React, {useCallback, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';

import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Button from '@material-ui/core/Button';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import {makeStyles, createStyles, Theme} from '@material-ui/core/styles';
import RemoveIcon from '@material-ui/icons/DeleteForever';

import {ALLOWED_FIELDS} from '@components/templated-message/templated-message.consts';

export interface FieldListProps {
  options: Array<{label: string; value: any}>;
  onChange: (newValue: ALLOWED_FIELDS) => void;
  onRemove: () => void;
  onClose: () => void;
  open: boolean;
  isCreate: boolean;
  style: any;
}

const el = document.createElement('div');

export const FieldList: React.FC<FieldListProps> = ({
  open,
  onClose,
  onChange,
  onRemove,
  options,
  isCreate,
  style,
}) => {
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    setIsShown(open);
  }, [open]);

  useEffect(() => {
    const body = document.getElementsByTagName('body');
    body[0]?.appendChild(el);
    return () => {
      body[0]?.removeChild(el);
    };
  }, []);

  const handleClose = useCallback(() => {
    setIsShown(false);
    setTimeout(() => onClose(), 500);
  }, [onClose]);

  const handleChange = useCallback(
    (event: React.MouseEvent<{id: any}>) => {
      if (onChange) {
        onChange(event.currentTarget.id);
      }
      handleClose();
    },
    [onChange, handleClose]
  );

  const handleRemove = useCallback(
    (event: React.MouseEvent<{id: any}>) => {
      if (onRemove) {
        onRemove();
      }
      handleClose();
    },
    [onRemove, handleClose]
  );

  const handleListKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        handleClose();
      }
    },
    [handleClose]
  );

  const {buttons, menuStyle, paperStyle, removeBtn} = useStyles();
  return ReactDOM.createPortal(
    <Grow in={isShown}>
      <Paper style={style} className={paperStyle}>
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <MenuList
              className={menuStyle}
              dense
              autoFocusItem={isShown}
              onKeyDown={handleListKeyDown}>
              {options.map(({label, value}) => (
                <MenuItem key={label} id={value} onClick={handleChange}>
                  {label}
                </MenuItem>
              ))}
            </MenuList>
            {!isCreate && (
              <div className={buttons}>
                <Button onClick={handleClose} size="small">
                  Cancel
                </Button>
                <Button
                  onClick={handleRemove}
                  size="small"
                  startIcon={<RemoveIcon fontSize="small" />}
                  variant="outlined"
                  className={removeBtn}>
                  Remove
                </Button>
              </div>
            )}
          </div>
        </ClickAwayListener>
      </Paper>
    </Grow>,
    el
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttons: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-around',
      padding: theme.spacing(1),
      '& > button': {
        borderRadius: 24,
        flex: 1,
        margin: theme.spacing(0, 0.25),
      },
    },
    menuStyle: {
      overflowY: 'auto',
      maxHeight: 168,
    },
    paperStyle: {
      position: 'absolute',
      boxShadow: '3px 2px 20px rgba(0, 0, 0, 0.147447)',
      maxHeight: 224,
      zIndex: 1501,
    },
    removeBtn: {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
    },
  })
);
